/**
 * Animation Scenarios - User-Friendly Animation Definitions
 *
 * This file contains simple, clear scenario definitions that are easy to read and edit.
 * All complex animation logic has been moved to AnimationBuilder.ts
 *
 * Coordinate System: 0-indexed, bottom-left is (0,0), top-right is (7,4)
 * All coordinates match the yellow tags exactly.
 */

import { AnimationSequence } from './PieceRulesAnimator';
import { SimpleScenario, buildAnimationFromScenario, buildCaptureAnimationFromScenario, buildZombificationAnimationFromScenario, buildEntangleAnimationFromScenario, buildInkAnimationFromScenario, buildPenguinMovementAnimationFromScenario, buildPenguinRotationAnimationFromScenario, buildPenguinCaptureRotationAnimationFromScenario, EnhancedAnimationSequence } from './AnimationBuilder';
import { getCurrentLanguage, getTranslation } from '../Providers/LanguageContext';

// Helper function to get translated scenario text
const t = (key: string): string => {
  return getTranslation(getCurrentLanguage(), key);
};

// =============================================================================
// BEAR ANIMATIONS
// =============================================================================

const bearMovementScenario = (): SimpleScenario => ({
  name: "Bear Movement Demonstration",
  description: t('scenarios.bearMovement'),
  fen: "8/2W1T3/3B4/2c5/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing movement intention (adjusted +1)
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square (adjusted +1)
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.bearMovementPost'),
  type: 'movement'
});

const bearSavageScenario = (): SimpleScenario => ({
  name: "Bear Savage Demonstration",
  description: t('scenarios.bearSavage'),
  fen: "8/8/3Br3/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing movement intention
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.bearSavagePost'),
  type: 'ability'
});

// =============================================================================
// CRANE ANIMATIONS
// =============================================================================

const craneMovementScenario = (): SimpleScenario => ({
  name: "Crane Movement Demonstration",
  description: t('scenarios.craneMovement'),
  fen: "8/3B4/3C4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [5, 3],    // Square animation showing movement intention
  travelAnimation: [5, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [5, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.craneMovementPost'),
  type: 'movement'
});

const craneBirdTypeScenario = (): SimpleScenario => ({
  name: "Crane Bird Type Demonstration",
  description: t('scenarios.craneBirdType'),
  fen: "5r2/8/1t1C1b02/8/5w2",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing movement intention
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.craneBirdTypePost'),
  type: 'ability'
});

// =============================================================================
// FROG ANIMATIONS
// =============================================================================

const frogBasicMovementScenario = (): SimpleScenario => ({
  name: "Frog Regular Movement Demonstration",
  description: t('scenarios.frogMovement'),
  fen: "8/8/3F4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [5, 5],    // Square animation showing movement intention
  travelAnimation: [5, 5],    // Circle animation signifying AI clicking square
  pieceMovement: [5, 4],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.frogMovementPost'),
  type: 'movement'
});

const frogWallBlockScenario = (): SimpleScenario => ({
  name: "Frog Wall Block Demonstration",
  description: t('scenarios.frogWallBlock'),
  fen: "8/3F4/8/1W3r2/8",
  selectAnimation: [3, 1],    // Circle animation when piece is first selected
  selectPiece: [3, 1],        // Square to select piece in game engine
  travelIndicator: [4, 0],    // Square animation showing movement intention
  travelAnimation: [4, 0],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 0],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.frogWallBlockPost'),
  type: 'movement'
});

const frogWallClimbScenario = (): SimpleScenario => ({
  name: "Frog Wall Climb Demonstration",
  description: t('scenarios.frogWallClimb'),
  fen: "3F4/8/8/8/8",
  selectAnimation: [3, 0],    // Circle animation when piece is first selected
  selectPiece: [3, 0],        // Square to select piece in game engine
  travelIndicator: [4, 0],    // Square animation showing movement intention
  travelAnimation: [4, 0],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 0],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.frogWallClimbPost'),
  type: 'movement'
});

const frogLilypadScenario = (): SimpleScenario => ({
  name: "Frog Lilypad Demonstration",
  description: t('scenarios.frogLilypad'),
  fen: "2c5/8/8/8/F7",
  selectAnimation: [0, 4],    // Circle animation when piece is first selected
  selectPiece: [0, 4],        // Square to select piece in game engine
  travelIndicator: [2, 0],    // Square animation showing movement intention
  travelAnimation: [2, 0],    // Circle animation signifying AI clicking square
  pieceMovement: [2, 0],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.frogLilypadPost'),
  type: 'ability'
});

const frogPoisonousScenario = (): SimpleScenario => ({
  name: "Frog Poisonous Demonstration",
  description: t('scenarios.frogPoisonous'),
  fen: "8/8/3F4/4m3/8",
  selectAnimation: [4, 3],    // Circle animation when piece is first selected
  selectPiece: [4, 3],        // Square to select piece in game engine
  travelIndicator: [3, 2],    // Square animation showing movement intention
  travelAnimation: [3, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.frogPoisonousPost'),
  type: 'ability'
});

// =============================================================================
// FUNGI ANIMATIONS
// =============================================================================

const fungiBasicMovementScenario = (): SimpleScenario => ({
  name: "Fungi Regular Movement Demonstration",
  description: t('scenarios.fungiMovement'),
  fen: "8/8/3U4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing movement intention
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.fungiMovementPost'),
  type: 'movement'
});

const fungiMeadowScenario = (): SimpleScenario => ({
  name: "Fungi Meadow Demonstration",
  description: t('scenarios.fungiMeadow'),
  fen: "3U4/8/8/8/4t3",
  selectAnimation: [4, 4],    // Circle animation when piece is first selected
  selectPiece: [4, 4],        // Square to select piece in game engine
  travelIndicator: [4, 2],    // Square animation showing movement intention
  travelAnimation: [4, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.fungiMeadowPost'),
  type: 'ability'
});

const fungiParasiticScenario = (): SimpleScenario => ({
  name: "Fungi Parasitic Demonstration",
  description: t('scenarios.fungiParasitic'),
  fen: "8/8/3U4/8/4t3",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [3, 4],    // Square animation showing movement intention
  travelAnimation: [3, 4],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 3],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.fungiParasiticPost'),
  type: 'ability'
});

const fungiZombificationScenario = (): SimpleScenario => ({
  name: "Fungi Zombification Demonstration",
  description: t('scenarios.fungiZombification'),
  fen: "8/8/3U4/8/3c4",
  selectAnimation: [3, 4],    // Circle animation when piece is first selected
  selectPiece: [3, 4],        // Square to select piece in game engine
  travelIndicator: [3, 2],    // Square animation showing movement intention
  travelAnimation: [3, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 2],      // Engine completing move using game engine
  travelIndicator2: [2, 2],   // Second movement intention
  travelAnimation2: [2, 2],   // Second movement click
  pieceMovement2: [2, 2],     // Second movement execution
  postMoveDescription: t('scenarios.fungiZombificationPost'),
  type: 'ability'
});

const fungiFungiScenario = (): SimpleScenario => ({
  name: "Fungi Fungi Demonstration",
  description: t('scenarios.fungiFungi'),
  fen: "5v2/8/3Uf3/8/5o2",
  selectAnimation: [5, 4],    // Circle animation when piece is first selected
  selectPiece: [5, 4],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing movement intention
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 3],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.fungiFungiPost'),
  type: 'ability'
});

// =============================================================================
// GOAT ANIMATIONS
// =============================================================================

const goatBasicMovementScenario = (): SimpleScenario => ({
  name: "Goat Regular Movement Demonstration",
  description: t('scenarios.goatMovement'),
  fen: "8/8/3G4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing movement intention
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.goatMovementPost'),
  type: 'movement'
});

const goatTraverseScenario = (): SimpleScenario => ({
  name: "Goat Traverse Demonstration",
  description: t('scenarios.goatTraverse'),
  fen: "3T4/1CB1m/G7/8/8",
  selectAnimation: [0, 2],    // Circle animation when piece is first selected
  selectPiece: [0, 2],        // Square to select piece in game engine
  travelIndicator: [5, 3],    // Square animation showing movement intention
  travelAnimation: [5, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [5, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.goatTraversePost'),
  type: 'movement'
});

const goatCaptureScenario = (): SimpleScenario => ({
  name: "Goat Capture Demonstration",
  description: t('scenarios.goatCapture'),
  fen: "8/2b1w3/3G4/4m3/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 4],    // Square animation showing movement intention
  travelAnimation: [4, 4],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 3],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.goatCapturePost'),
  type: 'movement'
});

const goatFaintScenario = (): SimpleScenario => ({
  name: "Goat Faint Demonstration",
  description: t('scenarios.goatFaint'),
  fen: "8/8/3G4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [3, 3],    // Square animation showing movement intention
  travelAnimation: [3, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.goatFaintPost'),
  type: 'ability'
});

// =============================================================================
// MONKEY ANIMATIONS
// =============================================================================

const monkeyBasicMovementScenario = (): SimpleScenario => ({
  name: "Monkey Regular Movement Demonstration",
  description: t('scenarios.monkeyMovement'),
  fen: "8/8/3M4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing movement intention
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.monkeyMovementPost'),
  type: 'movement'
});

const monkeyLeapScenario = (): SimpleScenario => ({
  name: "Monkey Leap Demonstration",
  description: t('scenarios.monkeyLeap'),
  fen: "8/8/1MB1C1W1/8/8",
  selectAnimation: [1, 2],    // Circle animation when piece is first selected
  selectPiece: [1, 2],        // Square to select piece in game engine
  travelIndicator: [7, 3],    // Square animation showing movement intention
  travelAnimation: [7, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [7, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.monkeyLeapPost'),
  type: 'movement'
});

const monkeySwingScenario = (): SimpleScenario => ({
  name: "Monkey Swing Demonstration",
  description: t('scenarios.monkeySwing'),
  fen: "8/6w1/3MT3/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [5, 3],    // Square animation showing movement intention
  travelAnimation: [5, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [5, 2],      // Engine completing move using game engine
  travelIndicator2: [6, 1],   // Second movement intention
  travelAnimation2: [6, 2],   // Second movement click
  pieceMovement2: [6, 1],     // Second movement execution
  postMoveDescription: t('scenarios.monkeySwingPost'),
  type: 'movement'
});

const monkeyConsumeScenario = (): SimpleScenario => ({
  name: "Monkey Consume Demonstration",
  description: t('scenarios.monkeyConsume'),
  fen: "8/8/3M1p2/4U3/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 4],    // Square animation showing movement intention
  travelAnimation: [4, 4],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 3],      // Engine completing move using game engine
  travelIndicator2: [5, 2],   // Second movement intention
  travelAnimation2: [5, 2],   // Second movement click
  pieceMovement2: [5, 2],     // Second movement execution
  postMoveDescription: t('scenarios.monkeyConsumePost'),
  type: 'ability'
});

const monkeyGambleScenario = (): SimpleScenario => ({
  name: "Monkey Gamble Demonstration",
  description: t('scenarios.monkeyGamble'),
  fen: "8/8/3Mb3/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing movement intention
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.monkeyGamblePost'),
  type: 'ability'
});

// =============================================================================
// OCTOPUS ANIMATIONS
// =============================================================================

const octopusBasicMovementScenario = (): SimpleScenario => ({
  name: "Octopus Regular Movement",
  description: t('scenarios.octopusMovement'),
  fen: "8/8/8/3O4/8",
  selectAnimation: [3, 3],    // Circle animation when piece is first selected
  selectPiece: [3, 3],        // Square to select piece in game engine
  travelIndicator: [4, 4],    // Square animation showing movement intention
  travelAnimation: [4, 4],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 3],      // Engine completing move using game engine
  postMoveDescription: "",
  type: 'movement'
});

const octopusCaptureScenario = (): SimpleScenario => ({
  name: "Octopus Capture Demonstration",
  description: t('scenarios.octopusCapture'),
  fen: "8/8/3wm3/3O4/8",
  selectAnimation: [3, 3],    // Circle animation when piece is first selected
  selectPiece: [3, 3],        // Square to select piece in game engine
  travelIndicator: [3, 2],    // Square animation showing movement intention - targeting monkey square
  travelAnimation: [3, 2],    // Circle animation signifying AI clicking square - targeting monkey square
  pieceMovement: [3, 2],      // Engine completing move using game engine - targeting monkey square
  postMoveDescription: t('scenarios.octopusCapturePost'),
  type: 'movement'
});

const octopusEntangleScenario = (): SimpleScenario => ({
  name: "Octopus Entangle Demonstration",
  description: t('scenarios.octopusEntangle'),
  fen: "8/8/4b3/3O4/5c2",
  selectAnimation: [3, 3],    // Circle animation when piece is first selected
  selectPiece: [3, 3],        // Square to select piece in game engine
  travelIndicator: [4, 2],    // Square animation showing entangle intention
  travelAnimation: [4, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 3],      // Engine completing noMove (octopus stays in place)
  postMoveDescription: t('scenarios.octopusEntanglePost'),
  type: 'ability'
});

const octopusInkScenario = (): SimpleScenario => ({
  name: "Octopus Ink Demonstration",
  description: t('scenarios.octopusInk'),
  fen: "8/4m3/3O4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [3, 1],    // Square animation showing movement intention
  travelAnimation: [3, 1],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 1],      // Engine completing move using game engine
  travelIndicator2: [4, 3],   // Monkey attacking octopus's original position (shadow)
  travelAnimation2: [4, 3],   // Monkey clicking to attack the shadow
  pieceMovement2: [4, 2],     // Monkey moves to attack position (from [4,1])
  postMoveDescription: t('scenarios.octopusInkPost'),
  type: 'ability'
});

// =============================================================================
// RHINO ANIMATIONS
// =============================================================================

const rhinoBasicMovementScenario = (): SimpleScenario => ({
  name: "Rhino Regular Movement Demonstration",
  description: t('scenarios.rhinoMovement'),
  fen: "8/8/R7/8/8",
  selectAnimation: [0, 2],    // Circle animation when piece is first selected
  selectPiece: [0, 2],        // Square to select piece in game engine
  travelIndicator: [2, 2],    // Square animation showing movement intention
  travelAnimation: [2, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [2, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.rhinoMovementPost'),
  type: 'movement'
});

const rhinoBackStepScenario = (): SimpleScenario => ({
  name: "Rhino Back Step Demonstration",
  description: t('scenarios.rhinoBackStep'),
  fen: "8/8/3R4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [2, 1],    // Square animation showing movement intention
  travelAnimation: [2, 1],    // Circle animation signifying AI clicking square
  pieceMovement: [2, 1],      // Engine completing move using game engine
  postMoveDescription: "",
  type: 'movement'
});

const rhinoChargeScenario = (): SimpleScenario => ({
  name: "Rhino Charge Demonstration",
  description: t('scenarios.rhinoCharge'),
  fen: "8/8/3R2b1//8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [6, 2],    // Square animation showing movement intention
  travelAnimation: [6, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [6, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.rhinoChargePost'),
  type: 'ability'
});

const rhinoTrampleScenario = (): SimpleScenario => ({
  name: "Rhino Trample Demonstration",
  description: t('scenarios.rhinoTrample'),
  fen: "8/8/3Rt+mc2/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [5, 2],    // Square animation showing movement intention
  travelAnimation: [5, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [5, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.rhinoTramplePost'),
  type: 'ability'
});

const rhinoLaneScenario = (): SimpleScenario => ({
  name: "Rhino Lane Demonstration",
  description: t('scenarios.rhinoLane'),
  fen: "8/M7/R7/8/8",
  selectAnimation: [0, 2],    // Circle animation when piece is first selected
  selectPiece: [0, 2],        // Square to select piece in game engine
  travelIndicator: [0, 1],    // Square animation showing movement intention
  travelAnimation: [0, 1],    // Circle animation signifying AI clicking square
  pieceMovement: [0, 1],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.rhinoLanePost'),
  type: 'ability'
});

// =============================================================================
// SNAKE ANIMATIONS
// =============================================================================

const snakeRegularMovementScenario = (): SimpleScenario => ({
  name: "Snake Regular Movement Demonstration",
  description: t('scenarios.snakeMovement'),
  fen: "8/8/3S4/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [5, 2],    // Square animation showing movement intention
  travelAnimation: [5, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [5, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.snakeMovementPost'),
  type: 'movement'
});

const snakeMoveBlocksScenario = (): SimpleScenario => ({
  name: "Snake Move Blocks Demonstration",
  description: t('scenarios.snakeMoveBlocks'),
  fen: "8/2BS1r2/8/3C4/8",
  selectAnimation: [3, 1],    // Circle animation when piece is first selected
  selectPiece: [3, 1],        // Square to select piece in game engine
  travelIndicator: [3, 0],    // Square animation showing movement intention
  travelAnimation: [3, 0],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 0],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.snakeMoveBlocksPost'),
  type: 'movement'
});

const snakeStrikeScenario = (): SimpleScenario => ({
  name: "Snake Strike Demonstration",
  description: t('scenarios.snakeStrike'),
  fen: "8/8/3S2m1/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [6, 2],    // Square animation showing movement intention
  travelAnimation: [6, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [6, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.snakeStrikePost'),
  type: 'ability'
});

const snakeRebirthScenario = (): SimpleScenario => ({
  name: "Snake Shedskin Demonstration",
  description: t('scenarios.snakeShedskin'),
  fen: "8/8/3Sm3/8/8",
  selectAnimation: [4, 2],    // Circle animation when piece is first selected
  selectPiece: [4, 2],        // Square to select piece in game engine
  travelIndicator: [3, 3],    // Square animation showing movement intention
  travelAnimation: [3, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.snakeShedskinPost'),
  type: 'ability'
});

// =============================================================================
// PENGUIN ANIMATIONS
// =============================================================================

const penguinBasicMovementScenario = (): SimpleScenario => ({
  name: "Penguin Regular Movement Demonstration",
  description: t('scenarios.penguinMovement'),
  fen: "8/8/7P4/8/8",
  selectAnimation: [7, 2],    // Circle animation when piece is first selected
  selectPiece: [7, 2],        // Square to select piece in game engine
  travelIndicator: [0, 3],    // Square animation showing movement intention
  travelAnimation: [0, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [0, 2],      // Engine completing move using game engine
  travelIndicator2: [1, 1],   // Penguin choosing new facing direction
  travelAnimation2: [1, 2],   // Click to select rotation direction
  pieceMovement2: [0, 2],     // Penguin rotates in place at final position
  postMoveDescription: t('scenarios.penguinMovementPost'),
  type: 'movement'
});

const penguinRotateScenario = (): SimpleScenario => ({
  name: "Penguin Rotate Demonstration",
  description: t('scenarios.penguinRotate'),
  fen: "8/8/P7/8/8",
  selectAnimation: [0, 2],    // Circle animation when piece is first selected
  selectPiece: [0, 2],        // Square to select piece in game engine
  travelIndicator: [0, 4],    // Right direction for rotation
  travelAnimation: [0, 4],    // Click to rotate right
  pieceMovement: [0, 3],      // Penguin rotates but stays in same position
  postMoveDescription: t('scenarios.penguinRotatePost'),
  type: 'movement'
});

const penguinBirdTypeScenario = (): SimpleScenario => ({
  name: "Penguin Bird Type Demonstration",
  description: t('scenarios.penguinBirdType'),
  fen: "8/2w1b3/3P4/4c3/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 4],    // Target the crane for capture
  travelAnimation: [4, 4],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 3],      // Engine completing move using game engine
  travelIndicator2: [5, 2],   // Penguin choosing rotation direction after capture
  travelAnimation2: [5, 3],   // Click to select rotation direction
  pieceMovement2: [4, 4],     // Penguin rotates in place at final position
  postMoveDescription: t('scenarios.penguinBirdTypePost'),
  type: 'ability'
});

// =============================================================================
// WOLF ANIMATIONS
// =============================================================================

const wolfBasicMovementScenario = (): SimpleScenario => ({
  name: "Wolf Regular Movement Demonstration",
  description: t('scenarios.wolfMovement'),
  fen: "8/8/3W4/3C4/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 5],    // Square animation showing movement intention
  travelAnimation: [4, 5],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 4],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.wolfMovementPost'),
  type: 'movement'
});

const wolfLoneWolfScenario = (): SimpleScenario => ({
  name: "Wolf Lone Wolf Demonstration",
  description: t('scenarios.wolfLoneWolf'),
  fen: "8/8/8/3W4/8",
  selectAnimation: [3, 3],    // Circle animation when piece is first selected
  selectPiece: [3, 3],        // Square to select piece in game engine
  travelIndicator: [4, 0],    // Square animation showing movement intention
  travelAnimation: [4, 0],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 0],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.wolfLoneWolfPost'),
  type: 'ability'
});

// =============================================================================
// VIRUS ANIMATIONS
// =============================================================================

const virusRegularMovementScenario = (): SimpleScenario => ({
  name: "Virus Regular Movement Demonstration",
  description: t('scenarios.virusMovement'),
  fen: "8/8/3V4/8/3m4",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [3, 4],    // Square animation showing movement intention
  travelAnimation: [3, 4],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 4],      // Engine completing move using game engine
  postMoveDescription: "",
  type: 'movement'
});

const virusMoveBlocksScenario = (): SimpleScenario => ({
  name: "Virus Move Blocks Demonstration",
  description: t('scenarios.virusMoveBlocks'),
  fen: "2w5/3V4/8/5f2/8",
  selectAnimation: [3, 1],    // Circle animation when piece is first selected
  selectPiece: [3, 1],        // Square to select piece in game engine
  travelIndicator: [2, 0],    // Square animation showing movement intention
  travelAnimation: [2, 0],    // Circle animation signifying AI clicking square
  pieceMovement: [2, 0],      // Engine completing move using game engine
  postMoveDescription: "",
  type: 'movement'
});

const virusCaptureHostScenario = (): SimpleScenario => ({
  name: "Virus Capture Host Demonstration",
  description: t('scenarios.virusCaptureHost'),
  fen: "8/8/3V+m1b1/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [5, 2],    // Square animation showing movement intention
  travelAnimation: [5, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [5, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.virusCaptureHostPost'),
  type: 'ability'
});

const virusContagionScenario = (): SimpleScenario => ({
  name: "Virus Contagion Demonstration",
  description: t('scenarios.virusContagion'),
  fen: "3r1s2/8/1V6/M7/8",
  selectAnimation: [1, 2],    // Circle animation when piece is first selected
  selectPiece: [1, 2],        // Square to select piece in game engine
  travelIndicator: [3, 0],    // Square animation showing movement intention
  travelAnimation: [3, 0],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 0],      // Engine completing move using game engine
  travelIndicator2: [5, 0],   // Second movement intention
  travelAnimation2: [5, 0],   // Second movement click
  pieceMovement2: [5, 0],     // Second movement execution
  postMoveDescription: t('scenarios.virusContagionPost'),
  type: 'ability'
});

// =============================================================================
// TREE ANIMATIONS
// =============================================================================

const treeRegularMovementScenario = (): SimpleScenario => ({
  name: "Tree Regular Movement Demonstration",
  description: t('scenarios.treeMovement'),
  fen: "8/8/8/3T4/8",
  selectAnimation: [3, 3],    // Circle animation when piece is first selected
  selectPiece: [3, 3],        // Square to select piece in game engine
  travelIndicator: [3, 0],    // Square animation showing movement intention
  travelAnimation: [3, 0],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 0],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.treeMovementPost'),
  type: 'movement'
});

const treeHouseScenario = (): SimpleScenario => ({
  name: "Tree House Demonstration",
  description: t('scenarios.treeHouse'),
  fen: "8/8/3T4/3C4/8",
  selectAnimation: [3, 3],    // Circle animation when piece is first selected
  selectPiece: [3, 3],        // Square to select piece in game engine
  travelIndicator: [3, 3],    // Square animation showing movement intention
  travelAnimation: [3, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [3, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.treeHousePost'),
  type: 'ability'
});

const treeTrampleScenario = (): SimpleScenario => ({
  name: "Tree Trample Demonstration",
  description: t('scenarios.treeTrample'),
  fen: "8/8/3Tm+v3/8/8",
  selectAnimation: [3, 2],    // Circle animation when piece is first selected
  selectPiece: [3, 2],        // Square to select piece in game engine
  travelIndicator: [4, 2],    // Square animation showing movement intention
  travelAnimation: [4, 2],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 2],      // Engine completing move using game engine
  postMoveDescription: t('scenarios.treeTramplePost'),
  type: 'ability'
});

const treePlantScenario = (): SimpleScenario => ({
  name: "Tree Plant Demonstration",
  description: t('scenarios.treePlant'),
  fen: "5v2/8/3Tf3/8/5o2",
  selectAnimation: [5, 4],    // Circle animation when piece is first selected
  selectPiece: [5, 4],        // Square to select piece in game engine
  travelIndicator: [4, 3],    // Square animation showing staying in place
  travelAnimation: [4, 3],    // Circle animation signifying AI clicking square
  pieceMovement: [4, 3],      // Engine completing move using game engine (staying in place)
  postMoveDescription: t('scenarios.treePlantPost'),
  type: 'ability'
});

// =============================================================================
// EXPORT ANIMATION DATABASE
// =============================================================================

export function generateRandomBearMovementAnimation(): EnhancedAnimationSequence {
  return buildAnimationFromScenario(bearMovementScenario());
}

export function getPieceAnimationDatabase() {
  return {
  Bear: {
    movement: buildAnimationFromScenario(bearMovementScenario()),
    savage: buildAnimationFromScenario(bearSavageScenario())  // Using regular animation for now
  },
  Crane: {
    movement: buildAnimationFromScenario(craneMovementScenario()),
    "Bird Type": buildAnimationFromScenario(craneBirdTypeScenario()),
  },
  Frog: {
    movement: buildAnimationFromScenario(frogBasicMovementScenario()),
    "Move Blocks": buildAnimationFromScenario(frogWallBlockScenario()),
    "Wall Climb": buildAnimationFromScenario(frogWallClimbScenario()),
    "Lilypad": buildAnimationFromScenario(frogLilypadScenario()),
    "Poisonous": buildAnimationFromScenario(frogPoisonousScenario()),
  },
  Fungi: {
    movement: buildAnimationFromScenario(fungiBasicMovementScenario()),
    "Meadow": buildAnimationFromScenario(fungiMeadowScenario()),
    "Parasitic Fungi": buildAnimationFromScenario(fungiParasiticScenario()),
    "Zombification": buildZombificationAnimationFromScenario(fungiZombificationScenario()),
    "Fungi": buildAnimationFromScenario(fungiFungiScenario()),
  },
  Goat: {
    movement: buildAnimationFromScenario(goatBasicMovementScenario()),
    "Traverse": buildAnimationFromScenario(goatTraverseScenario()),
    "Capture": buildAnimationFromScenario(goatCaptureScenario()),
    "Faint": buildAnimationFromScenario(goatFaintScenario()),
  },
  Monkey: {
    movement: buildAnimationFromScenario(monkeyBasicMovementScenario()),
    "Leap": buildAnimationFromScenario(monkeyLeapScenario()),
    "Swing": buildAnimationFromScenario(monkeySwingScenario()),
    "Consume": buildAnimationFromScenario(monkeyConsumeScenario()),
    "Gamble": buildAnimationFromScenario(monkeyGambleScenario()),
  },
  Octopus: {
    movement: buildAnimationFromScenario(octopusBasicMovementScenario()),
    "Capture": buildCaptureAnimationFromScenario(octopusCaptureScenario()),
    "Entangle": buildAnimationFromScenario(octopusEntangleScenario()),
    "Ink": buildInkAnimationFromScenario(octopusInkScenario()),
  },
  Penguin: {
    movement: buildPenguinMovementAnimationFromScenario(penguinBasicMovementScenario()),
    "Rotate": buildPenguinRotationAnimationFromScenario(penguinRotateScenario()),
    "Bird Type": buildPenguinCaptureRotationAnimationFromScenario(penguinBirdTypeScenario()),
  },
  Rhino: {
    movement: buildAnimationFromScenario(rhinoBasicMovementScenario()),
    "Back Step": buildAnimationFromScenario(rhinoBackStepScenario()),
    "Trample": buildAnimationFromScenario(rhinoTrampleScenario()),
    "Charge": buildAnimationFromScenario(rhinoChargeScenario()),
    "Lane": buildAnimationFromScenario(rhinoLaneScenario()),
  },
  Snake: {
    movement: buildAnimationFromScenario(snakeRegularMovementScenario()),
    "Move Blocks": buildAnimationFromScenario(snakeMoveBlocksScenario()),
    "Snake Strike": buildAnimationFromScenario(snakeStrikeScenario()),
    "Shedskin": buildAnimationFromScenario(snakeRebirthScenario()),
  },
  Tree: {
    movement: buildAnimationFromScenario(treeRegularMovementScenario()),
    "House": buildAnimationFromScenario(treeHouseScenario()),
    "Trample": buildAnimationFromScenario(treeTrampleScenario()),
    "Plant": buildAnimationFromScenario(treePlantScenario()),
  },
  Virus: {
    movement: buildAnimationFromScenario(virusRegularMovementScenario()),
    "Move Blocks": buildAnimationFromScenario(virusMoveBlocksScenario()),
    "Capture Host": buildAnimationFromScenario(virusCaptureHostScenario()),
    "Contagion": buildAnimationFromScenario(virusContagionScenario()),
  },
  Wolf: {
    movement: buildAnimationFromScenario(wolfBasicMovementScenario()),
    "Lone Wolf": buildAnimationFromScenario(wolfLoneWolfScenario())
  }
  };
}

// Export a constant for backwards compatibility
export const PieceAnimationDatabase = getPieceAnimationDatabase();

// =============================================================================
// QUICK REFERENCE FOR PLANNING 50 SCENARIOS
// =============================================================================

/*

TEMPLATE FOR NEW SCENARIOS:

const newScenario = (): SimpleScenario => ({
  name: "Piece Action Demonstration",
  description: "Brief description of what this shows",
  fen: "8/8/8/8/8",  // FEN string for piece placement
  selectSquare: [row, col],  // Which piece to select (yellow tag coordinates)
  travelSquare: [row, col],  // Where to move/target (yellow tag coordinates)
  moveDescription: "User-friendly explanation of what the piece can do",
  type: 'movement' | 'capture' | 'ability'
});

COORDINATE REFERENCE:
- Bottom-left corner is (0,0)
- Top-right corner is (7,4)
- Always use the yellow tag coordinates
- Row 0 = bottom, Row 7 = top
- Col 0 = left, Col 4 = right

FEN PIECE SYMBOLS:
- B/b = Bear
- C/c = Crane
- F/f = Frog
- U/u = Fungi
- G/g = Goat
- M/m = Monkey
- O/o = Octopus
- H/h = Octopus Shadow
- P/p = Penguin
- R/r = Rhino
- S/s = Snake
- T/t = Tree
- V/v = Virus
- W/w = Wolf
- A/a = Fainted Goat
- Uppercase = Light pieces
- Lowercase = Dark pieces

*/